// Reads color tokens from the :root custom properties defined in
// src/Styles/tokens — those files are the single source of truth for every
// color in the app, in both the light and the dark theme. Values are read at
// call time, so they reflect whichever theme is active on <html> right now.
// This accessor exists only for consumers that can't resolve CSS var()
// themselves: chart configs (ECharts renders to canvas, not the DOM, so it
// needs real literal strings at the time the option object is built).
// Everywhere else — plain CSS and inline `style={{}}` props — use
// `var(--hc-x)` directly instead of importing this file.
export const cssVar = (name, fallback = "") => {
  if (typeof window === "undefined" || typeof document === "undefined") return fallback;
  return getComputedStyle(document.documentElement).getPropertyValue(name).trim() || fallback;
};

export const theme = {
  chartCategorical: () =>
    Array.from({ length: 10 }, (_, i) => cssVar(`--hc-chart-${i + 1}`)),
  chartTitleText: () => cssVar("--hc-chart-title-text", "#102a43"),
  chartSubtitleText: () => cssVar("--hc-chart-subtitle-text", "#64748b"),
  chartAxisLabel: () => cssVar("--hc-chart-axis-label", "#475569"),
  chartAxisLine: () => cssVar("--hc-chart-axis-line", "#cbd5e1"),
  chartGridLine: () => cssVar("--hc-chart-grid-line", "rgba(148, 163, 184, 0.24)"),
  chartTooltipText: () => cssVar("--hc-chart-tooltip-text", "#0f172a"),
  chartTooltipTextRgb: () => cssVar("--hc-chart-tooltip-text-rgb", "15, 23, 42"),
  chartTooltipBg: () => cssVar("--hc-chart-tooltip-bg", "rgba(255, 255, 255, 0.95)"),
  chartTooltipBorder: () => cssVar("--hc-chart-tooltip-border", "#d1d5db"),
  chartZoomTrackBorder: () => cssVar("--hc-chart-zoom-track-border", "#e2e8f0"),
  chartZoomTrackBg: () => cssVar("--hc-chart-zoom-track-bg", "#f8fafc"),
  chartWhite: () => cssVar("--hc-white", "#ffffff"),
  // Gap color between pie slices / bars / map regions: the card behind the chart.
  chartSurface: () => cssVar("--hc-surface", "#ffffff"),
  chartRadarAreas: () => [
    cssVar("--hc-chart-radar-area-1", "rgba(240, 251, 252, 0.6)"),
    cssVar("--hc-chart-radar-area-2", "rgba(255, 255, 255, 0.8)"),
  ],
  chartSuccess: () => cssVar("--hc-chart-8", "#22c55e"),
  chartHighlight: () => cssVar("--hc-chart-highlight", "#4f7cf7"),
  chartHighlightEmphasis: () => cssVar("--hc-chart-highlight-emphasis", "#2c65d5"),
  chartAreaLight: () => cssVar("--hc-chart-area-light", "#eaf2ff"),
  chartDayLabel: () => cssVar("--hc-chart-day-label", "#94a3b8"),
  chartBlue100: () => cssVar("--hc-blue-100", "#dbeafe"),
  chartBlue500: () => cssVar("--hc-blue-500", "#3b82f6"),
  chartBlue600: () => cssVar("--hc-blue-600", "#2563eb"),
  chartBlue700: () => cssVar("--hc-blue-700", "#1d4ed8"),
  chartBlue800: () => cssVar("--hc-blue-800", "#1e40af"),

  // Value-Based Care tokens
  vbcHeroGradient: () =>
    cssVar(
      "--hc-vbc-hero-gradient",
      "linear-gradient(135deg, #014c54 0%, #3c9fa2 58%, #1cbdaa 100%)"
    ),
  vbcAgeBandColors: () => [
    cssVar("--hc-vbc-age-0-3", "#3b82f6"),
    cssVar("--hc-vbc-age-4-9", "#06b6d4"),
    cssVar("--hc-vbc-age-10-12", "#d97706"),
    cssVar("--hc-vbc-age-13-17", "#6366f1"),
    cssVar("--hc-vbc-age-18-21", "#ec4899"),
    cssVar("--hc-vbc-age-22-30", "#059669"),
    cssVar("--hc-vbc-age-31-40", "#ea580c"),
    cssVar("--hc-vbc-age-41-50", "#1e40af"),
    cssVar("--hc-vbc-age-51-60", "#65a30d"),
    cssVar("--hc-vbc-age-61-70", "#86198f"),
    cssVar("--hc-vbc-age-71-80", "#0f766e"),
    cssVar("--hc-vbc-age-81-plus", "#dc2626"),
  ],
  vbcHeatmapEmpty: () => cssVar("--hc-vbc-heatmap-empty", "#e2e8f0"),
  vbcHeatmapEmptyBorder: () => cssVar("--hc-vbc-heatmap-empty-border", "#cbd5e1"),
  vbcTooltipDot: () => cssVar("--hc-vbc-tooltip-dot", "#38bdf8"),
  vbcPdfHeader: () => cssVar("--hc-vbc-pdf-header", "#0E7A80"),
  vbcPdfStripe: () => cssVar("--hc-vbc-pdf-stripe", "#f4f9fa"),
  vbcPdfBorder: () => cssVar("--hc-vbc-pdf-border", "#b4c3cd"),
  vbcExcelHeader: () => cssVar("--hc-vbc-excel-header", "#0E7A80"),
  vbcExcelStripe: () => cssVar("--hc-vbc-excel-stripe", "#f0f7f8"),

  // Generic (non-chart) palette reads
  blue100: () => cssVar("--hc-blue-100", "#dbeafe"),
  blue200: () => cssVar("--hc-blue-200", "#bfdbfe"),
  blue300: () => cssVar("--hc-blue-300", "#93c5fd"),
  blue400: () => cssVar("--hc-blue-400", "#60a5fa"),
  blue500: () => cssVar("--hc-blue-500", "#3b82f6"),

  // Brand gradient reads (for canvas consumers — e.g. the Restricted page's
  // particle field — that can't resolve the CSS var() gradient directly).
  gradientStart: () => cssVar("--hc-gradient-start", "#014c54"),
  gradientMid: () => cssVar("--hc-gradient-mid", "#3c9fa2"),
  gradientEnd: () => cssVar("--hc-gradient-end", "#f5b942"),

  // Fee schedule chart tokens
  feeTealLight: () => cssVar("--hc-fee-teal-light", "#eef9fa"),
  feeTealMid: () => cssVar("--hc-fee-teal-mid", "#8de0e3"),
  feeTeal: () => cssVar("--hc-fee-teal", "#26959a"),
  feeTealDark: () => cssVar("--hc-fee-teal-dark", "#134e52"),
};

export default theme;
